import React, {useEffect, useState} from 'react';
import { observer } from 'mobx-react-lite';
import SimpleTable from '../../components/SimpleTable';
import {Header, Loading} from '../../components';
import {Manager} from "../../core";
import { NotificationsStore } from '../../stores/notifications/notificationsStore';
import {Colors} from "../../styles";
import { AiOutlineDelete } from 'react-icons/ai';

const formatDateTime = (dateStr: string) => {
	if (!dateStr || dateStr === '-') return '-';
	try {
		const isoStr = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
		const utcStr = isoStr.endsWith('Z') || isoStr.includes('+') ? isoStr : `${isoStr}Z`;
		const date = new Date(utcStr);
		return date.toLocaleString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		});
	} catch (e) {
		return dateStr;
	}
};

export const Campaigns = observer(() => {
	const { list, get, delete: deleteNotification } = NotificationsStore();
	const [loading, setLoading] = useState(false);
	const [selectedNotification, setSelectedNotification] = useState<any>(null);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);

	useEffect( () => {
		setLoading(true)
		const handlerRequest = async () => {
			try {
				await get();
				setLoading(false);
			} catch (e) {
				setLoading(false);
				Manager.RequestError('error.campaigns.handlerRequest', e);
			}
		};

		handlerRequest().then((r) => r);
	}, [get]);

	const handleDelete = async (id: number) => {
		try {
			setLoading(true);
			await deleteNotification({ id });
			await get();
			setShowConfirmDelete(false);
			setSelectedNotification(null);
			setLoading(false);
		} catch (err) {
			setLoading(false);
			Manager.RequestError('error.campaigns.handleDelete', err);
		}
	};

	const campaignsGrid = [
		{
			field: 'type',
			headerText: 'Tipo',
			width: '120',
			textAlign: 'Center',
			template: (row: any) => (
				<span className={`px-2 py-1 rounded-md text-xs font-bold ${row.userId ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
					{row.userId ? 'Individual' : 'Geral'}
				</span>
			)
		},
		{
			field: 'userName',
			headerText: 'Usuário',
			width: '180',
			textAlign: 'Center',
			template: (row: any) => {
				if (!row.userId) return 'Todos os Usuários';
				return row.userName || `Usuário ID: ${row.userId}`;
			}
		},
		{
			field: 'title',
			headerText: 'Título',
			width: '200',
			textAlign: 'Left'
		},
		{
			field: 'read',
			headerText: 'Status de Leitura',
			width: '150',
			textAlign: 'Center',
			template: (row: any) => {
				if (!row.userId) return <span className="text-gray-400">-</span>;
				return (
					<span className={`px-2 py-1 rounded-md text-xs font-bold ${row.read ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>
						{row.read ? 'Lida' : 'Não Lida'}
					</span>
				);
			}
		},
		{
			field: 'createdAt',
			headerText: 'Data',
			width: '180',
			textAlign: 'Center',
			template: (row: any) => formatDateTime(row.createdAt),
		},
		{
			headerText: 'Ações',
			width: '100',
			textAlign: 'Center',
			template: (row: any) => (
				<button 
					onClick={(e) => {
						e.stopPropagation();
						setSelectedNotification(row);
						setShowConfirmDelete(true);
					}}
					className="text-red-500 hover:text-red-700 text-xl"
				>
					{React.createElement(AiOutlineDelete as any)}
				</button>
			)
		}
	];

	return (
		<div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white dark:bg-secondary-dark-bg dark:text-gray-200 rounded-3xl relative">
			{loading && (
				<div className="absolute inset-0 bg-white dark:bg-secondary-dark-bg bg-opacity-50 dark:bg-opacity-80 z-50 flex items-center justify-center">
					<Loading backgroundColor={Colors.light} type={'bubbles'} color={Colors.green_darker} />
				</div>
			)}
			<Header category="Marketing" title="Campanhas de Notificação"/>
			
			<div className="mt-5">
				<SimpleTable 
					columns={campaignsGrid as any} 
					data={list} 
					pageSize={10} 
				/>
			</div>

			{showConfirmDelete && selectedNotification && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
					<div className="bg-white dark:bg-[#33373E] p-6 rounded-2xl w-full max-w-sm shadow-xl">
						<h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-100">Confirmar Exclusão</h2>
						<p className="text-gray-600 dark:text-gray-300 mb-6">
							Tem certeza que deseja deletar esta notificação? Esta ação não pode ser desfeita.
						</p>
						<div className="flex gap-4">
							<button 
								onClick={() => handleDelete(selectedNotification.id)}
								className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 rounded-lg"
							>
								Sim, Deletar
							</button>
							<button 
								onClick={() => setShowConfirmDelete(false)}
								className="flex-1 bg-gray-400 hover:bg-gray-500 text-white font-bold py-2 rounded-lg"
							>
								Cancelar
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
});
